import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { EmojiPicker } from "./EmojiPicker";
import { holdPlacement, type HoldPlace } from "../lib/holdPlacement";
import type { MessageRemoval } from "../lib/messages";

/* THE HELD MESSAGE (his 10-15 word, with an Instagram still): "each message has 3 dots?? No man! In insta, if we hold a message
 * it shows this so message moves a bit up and shows the emojis on top and other options on the bottom while blurring the
 * others". The ⋯ beside every bubble is GONE (on a touch screen it was drawn on EVERY message — `@media (hover: none)`). A
 * press held `HOLD_MS`, a right-click, or Enter / the menu key on a focused bubble opens THIS: a modal `<dialog>` (the top
 * layer — focus is held inside, Escape closes, the page behind is inert) whose `::backdrop` blurs the thread; a COPY of the
 * held bubble lifted where it was; the six above it on its side; a card below — Reply, Copy text, Unsend / Delete for you (no day or time: his 10-16 word "the date is not needed") — Reply
 * and Copy text. HIG (context menus): the actions of ONE item, reached by a long press or a secondary click, without
 * cluttering the interface — the phone's bubble already does exactly this through SwiftUI's `.contextMenu`. */

export function MessageHoldMenu({ anchorId, mine, picks, chosen, onReact, onReply, copyText, removal, onUnsend, onDeleteForMe, onClose }: {
  /** The held bubble's element id (`msg-<id>`) — copied, measured, and given the focus back on close. */
  anchorId: string;
  /** The coach's own message: its pieces share the bubble's RIGHT edge. */
  mine: boolean;
  /** The six, or null where the reactions table is not live (no bar at all). */
  picks: readonly string[] | null;
  /** The reader's own emoji on this message, if any. */
  chosen: string | null;
  onReact: (emoji: string) => void;
  onReply: () => void;
  /** The words Copy text copies; null = nothing to copy (a voice note), no item. */
  copyText: string | null;
  /** His 10-16 word: Unsend (mine, unseen) or Delete for you (seen, or theirs) — `messageRemoval`; "none" = neither. */
  removal: MessageRemoval;
  onUnsend: () => void;
  onDeleteForMe: () => void;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const slot = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);
  /** The release of the press that OPENED the menu lands on the backdrop; only a press that began inside closes it. */
  const pressedInside = useRef(false);
  /** Reply sends the focus to the composer; every other way out gives it back to the held bubble. MEASURED: focusing the
   *  composer while the modal is open fails (the page behind is inert) — so Reply closes the dialog FIRST. */
  const focusBack = useRef(true);
  const [place, setPlace] = useState<HoldPlace | null>(null);
  /** His 10-15 word: the `+` after the six opens EVERY emoji (`EmojiPicker`) — the bar and the card give way to it. */
  const [picking, setPicking] = useState(false);

  useLayoutEffect(() => {
    const d = dialog.current;
    const anchor = document.getElementById(anchorId);
    if (d === null || anchor === null) { onClose(); return; }
    const r = anchor.getBoundingClientRect();
    // A COPY, never the bubble itself: the thread keeps its node (scroll position, focus target, the jump-to flash).
    const copy = anchor.cloneNode(true) as HTMLElement;
    copy.removeAttribute("id");
    copy.removeAttribute("tabindex");
    copy.removeAttribute("title");
    copy.querySelectorAll("[id]").forEach((n) => n.removeAttribute("id"));
    copy.classList.remove("is-flash");
    copy.setAttribute("aria-hidden", "true");
    copy.style.width = `${String(r.width)}px`;
    slot.current?.replaceChildren(copy);
    if (!d.open) d.showModal();
    const b = bar.current;
    const c = card.current;
    setPlace(holdPlacement({ top: r.top, left: r.left, width: r.width, height: r.height },
      b === null ? null : { width: b.offsetWidth, height: b.offsetHeight },
      { width: c?.offsetWidth ?? 0, height: c?.offsetHeight ?? 0 },
      mine, { width: window.innerWidth, height: window.innerHeight }));
    // Measured once, at open: the menu answers ONE press.
  }, []);

  // Opened, not pointed at: the first emoji (or Reply where there are none) takes the focus, as a menu's first item does —
  // AFTER the pieces are placed (a `visibility: hidden` button cannot take focus; MEASURED: the dialog itself kept it).
  useEffect(() => {
    if (place !== null) dialog.current?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
  }, [place]);

  // Closing hands the focus back to the bubble that was held (a keyboard user lands where they were).
  useEffect(() => () => { if (focusBack.current) document.getElementById(anchorId)?.focus({ preventScroll: true }); }, [anchorId]);

  function onKey(e: React.KeyboardEvent) {
    // The picker's search field keeps its own arrows (the caret); so does the picker's grid while it is open.
    if (picking || e.target instanceof HTMLInputElement) return;
    const step = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : e.key === "ArrowUp" || e.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) return;
    e.preventDefault();
    const items = [...(dialog.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const at = items.findIndex((x) => x === document.activeElement);
    items[(at + step + items.length) % items.length]?.focus();
  }

  const hidden = place === null ? { visibility: "hidden" as const } : {};
  return (
    <dialog ref={dialog} className="inbox-hold" aria-label="Message"
            onCancel={(e) => { e.preventDefault(); onClose(); }}
            onPointerDown={() => { pressedInside.current = true; }}
            onClick={(e) => { if (e.target === e.currentTarget && pressedInside.current) onClose(); }}
            onKeyDown={onKey}>
      {picks !== null && !picking && (
        <div ref={bar} className="inbox-hold-bar" role="group" aria-label="React with"
             style={{ ...hidden, top: place?.bar?.top, left: place?.bar?.left }}>
          {picks.map((e) => (
            <button key={e} type="button" className={e === chosen ? "inbox-hold-pick is-mine" : "inbox-hold-pick"}
                    aria-pressed={e === chosen} onClick={() => { onReact(e); onClose(); }}>{e}</button>
          ))}
          {/* An emoji outside the six that is the reader's own shows here, marked, so it can be taken back in one tap. */}
          {chosen !== null && !picks.includes(chosen) && (
            <button type="button" className="inbox-hold-pick is-mine" aria-pressed="true"
                    onClick={() => { onReact(chosen); onClose(); }}>{chosen}</button>
          )}
          <button type="button" className="inbox-hold-pick inbox-hold-more" aria-label="More emoji"
                  onClick={() => setPicking(true)}>+</button>
        </div>
      )}
      {picking && picks !== null && (
        <EmojiPicker six={picks} chosen={chosen} onPick={(e) => { onReact(e); onClose(); }} />
      )}
      <div ref={slot} className={mine ? "inbox-hold-bubble is-mine" : "inbox-hold-bubble"} hidden={picking}
           style={{ ...hidden, top: place?.bubble.top, left: place?.bubble.left, height: place?.bubbleHeight }} />
      <div ref={card} className="inbox-hold-card" role="menu" aria-label="Message actions" hidden={picking}
           style={{ ...hidden, top: place?.card.top, left: place?.card.left }}>
        <button type="button" role="menuitem" className="inbox-hold-item" onClick={() => { focusBack.current = false; dialog.current?.close(); onReply(); onClose(); }}>
          <Icon name="reply" size={18} /> Reply
        </button>
        {copyText !== null && (
          <button type="button" role="menuitem" className="inbox-hold-item"
                  onClick={() => { void navigator.clipboard?.writeText(copyText).catch(() => undefined); onClose(); }}>
            <Icon name="copy" size={18} /> Copy text
          </button>
        )}
        {removal !== "none" && (
          <button type="button" role="menuitem" className="inbox-hold-item is-destructive"
                  onClick={() => { onClose(); if (removal === "unsend") onUnsend(); else onDeleteForMe(); }}>
            <Icon name={removal === "unsend" ? "undo" : "trash"} size={18} /> {removal === "unsend" ? "Unsend" : "Delete for you"}
          </button>
        )}
      </div>
    </dialog>
  );
}
